import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import { useOfferBuilderContext } from '../OfferBuilderContext';
import { useRateCalculations } from '../useRateCalculations';
import DraftTakerMarketRateDisplay from './DraftTakerMarketRateDisplay';

const TakerMarketRateDisplay = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const { getRateComparison } = useRateCalculations();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const {
    state: { builderVariant },
  } = useOfferBuilderContext();

  if (builderVariant === 'draft') {
    return <DraftTakerMarketRateDisplay takerMarket={takerMarket} />;
  }

  // only show side by side rate display for price discovery markets when in custom mode
  if (takerMarket.offerConfig.offerType === 'PREFERRED_TERM' || takerMarket.marketPricingType !== 'PRICE_DISCOVERY') {
    return <RateDisplay showDiscountAmount={false} takerMarket={takerMarket} />;
  }

  const { originalRate, originalRateType, workingRate, workingRateType, hasWorkingState, hasRateChanged } =
    getRateComparison(takerMarket);

  const originalRateTypeTranslation =
    originalRateType === 'DISC' ? t('core.discountAbbreviation') : t('core.aprAbbreviation');

  if (hasWorkingState && workingRate && workingRateType) {
    const workingRateTypeTranslation =
      workingRateType === 'DISC' ? t('core.discountAbbreviation') : t('core.aprAbbreviation');

    return (
      <div className="flex flex-wrap items-center gap-x-1">
        {/* if original rate is different than working, show side by side */}
        {hasRateChanged && originalRate !== undefined && (
          <span className="flex items-center gap-1 uppercase text-text-secondary">
            {offerDiscountAPRDisplay(originalRate, originalRateTypeTranslation)}
            <ArrowRightIcon className="fill-text-secondary" />
          </span>
        )}
        <span className="uppercase">{offerDiscountAPRDisplay(workingRate, workingRateTypeTranslation)}</span>
        {hasRateChanged && originalRate !== undefined && (
          <span className="-mt-1 size-2 rounded-full bg-lightBlue-500" />
        )}
      </div>
    );
  }

  // no working state, show original rate or indicate no rate set
  if (originalRate !== undefined) {
    return <span className="uppercase">{offerDiscountAPRDisplay(originalRate, originalRateTypeTranslation)}</span>;
  }

  return <span className="text-text-secondary">{t('builder.noRateSet')}</span>;
};

export default TakerMarketRateDisplay;
